import React, { useState, useContext } from 'react'
import { Button } from '@/components/ui/button'
import { Textarea } from '@/components/ui/textarea'
import { AuthContext } from '../../contexts/auth-context'
import { useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { createWalletNotification, sendBusinessOtp } from '../../services'
import { useNavigate } from 'react-router-dom'
import OtpVerification from '../../components/otp-verification/OtpVerification'
import OtpVerifyModal from '../../components/modals/otp-modal/OtpVerifyModal'

// SVG Icon for the Send Button
const SendIcon = (props) => (
  <svg
    {...props}
    xmlns="http://www.w3.org/2000/svg"
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="m22 2-7 20-4-9-9-4Z" />
    <path d="m22 2-11 11" />
  </svg>
)

function BusinessCustomerSendMessage() {
  const [message, setMessage] = useState('')
  const { auth, setOtp } = useContext(AuthContext)
  const { id, name, email } = useParams() // For sending to a specific customer

  const navigate = useNavigate()
  const [requireOtpVerification, setRequireOtpVerification] = useState(false)

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error('Please enter a message before sending.')
      return
    }

    const payload = {
      userLoyaltyCardId: id,
      messageBody: message,
    }

    try {
      const loadingToast = toast.loading('Sending message...')

      await createWalletNotification(payload)

      toast.dismiss(loadingToast)
      toast.success('Message sent successfully!')

      setMessage('')
    } catch (err) {
      toast.dismiss()

      // Handle OTP verification requirement
      if (err?.response?.status === 403 && err?.response?.data?.needVerification) {
        toast.warning('OTP verification required')

        try {
          await sendBusinessOtp()
          setRequireOtpVerification(true)
        } catch (otpError) {
          console.error('Failed to send OTP:', otpError)
          toast.error('Failed to send OTP. Please try again.')
        }
        return
      }

      // Handle other errors
      const errorMessage = err?.response?.data?.message || 'Failed to send message'
      toast.error(errorMessage)
    }
  }

  const currentTime = new Date().toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  const currentDayMonth = new Date().toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
  })

  // Message idea templates
  const messageIdeas = [
    "We haven't seen you in a while. Stop by for a cocktail on us!",
    'Happy birthday! Visit us this week and get a free dessert.',
    "Your car's ready to be picked up!",
    'Your table for 2 is confirmed for 8 PM tonight.',
  ]

  return (
    <div className="bg-slate-50 min-h-screen w-full font-sans p-4 sm:p-6 lg:p-8">
      <OtpVerifyModal
        isOpen={requireOtpVerification}
        onClose={() => {
          setRequireOtpVerification(false)
          setOtp(['', '', '', ''])
        }}
      >
        <OtpVerification
          isMessageVerify
          onClose={() => {
            setRequireOtpVerification(false)
            setOtp(['', '', '', ''])
          }}
          handleSendMessage={handleSendMessage}
        />
      </OtpVerifyModal>
      <div className="container mx-auto">
        {/* Go Back Button */}
        <div className="flex items-center ">
          <Button
            variant="outline"
            onClick={() => navigate(-1)}
            className="flex items-center gap-2"
          >
            ← Go Back
          </Button>
        </div>

        <div className="bg-white rounded-2xl   lg:grid lg:grid-cols-2 lg:gap-6">
          {/* Left Section: Message Composer */}
          <div className="p-8 md:p-12 space-y-8">
            <div>
              <h1 className="text-3xl font-bold tracking-tight text-slate-900">
                Send a Direct Message
              </h1>

              {name && email && (
                <div className="mt-3 inline-flex items-center px-3 py-1 rounded-full bg-indigo-50 border border-indigo-100 text-indigo-700 text-sm font-medium">
                  <span className="opacity-75 mr-1">To:</span>
                  {name} &lt;{email}&gt;
                </div>
              )}

              <p className="text-slate-600 mt-4 text-base leading-relaxed">
                This message will be sent directly to this customer's device. Use it for
                personalized updates, offers, or notifications.
              </p>
            </div>

            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-800">Message</h3>
              <div className="relative">
                <Textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="e.g. Hi Joe, your order is ready for pickup!"
                  className="w-full min-h-[140px] resize-y rounded-lg border-slate-300 p-4 text-base focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition"
                  maxLength={90}
                />
                <p className="absolute bottom-3 right-4 text-xs text-slate-500">
                  {message.length} / 90
                </p>
              </div>
            </div>

            <div className="space-y-4">
              <h3 className="font-semibold text-slate-800">Ideas</h3>
              <div className="flex flex-wrap gap-2">
                {messageIdeas.map((idea, index) => (
                  <button
                    key={index}
                    onClick={() => setMessage(idea)}
                    className="px-3 py-1.5 bg-slate-100 text-slate-700 text-sm rounded-full hover:bg-indigo-100 hover:text-indigo-700 transition-colors duration-200"
                  >
                    {idea}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                onClick={handleSendMessage}
                className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-semibold text-base py-3 px-6 rounded-lg shadow-md hover:shadow-lg hover:scale-105 transform-gpu transition-all duration-200 flex items-center gap-2 disabled:bg-slate-300 disabled:from-slate-300 disabled:to-slate-300 disabled:scale-100 disabled:cursor-not-allowed"
                disabled={!message.trim()}
              >
                <SendIcon className="h-5 w-5" />
                Send to Customer
              </Button>
            </div>
          </div>

          {/* Right Section: Realistic Phone Preview */}
          <div className="flex items-center justify-center bg-slate-100/70 p-8 rounded-b-2xl lg:rounded-r-2xl lg:rounded-bl-none">
            <div className="relative mx-auto border-gray-800 dark:border-gray-800 bg-gray-800 border-[10px] rounded-[2.5rem] h-[630px] w-[315px] shadow-2xl">
              {/* Phone Side Buttons */}
              <div className="h-[46px] w-[3px] bg-gray-800 absolute -start-[13px] top-[124px] rounded-s-lg"></div>
              <div className="h-[46px] w-[3px] bg-gray-800 absolute -start-[13px] top-[178px] rounded-s-lg"></div>
              <div className="h-[64px] w-[3px] bg-gray-800 absolute -end-[13px] top-[142px] rounded-e-lg"></div>

              <div className="rounded-[2rem] overflow-hidden w-full h-full bg-black">
                <div
                  className="relative w-full h-full bg-cover bg-center"
                  style={{
                    backgroundImage:
                      "url('https://images.unsplash.com/photo-1599891470333-653907474581?q=80&w=1887&auto=format&fit=crop')",
                  }}
                >
                  {/* Status Bar with Dynamic Island */}
                  <div className="absolute top-0 left-0 right-0 h-11 px-2 flex justify-between items-center z-10">
                    <span className="text-white text-sm font-semibold w-16 text-center">
                      {currentTime.split(' ')[0]}
                    </span>
                    <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-28 h-7 bg-black rounded-full"></div>
                    <div className="flex items-center space-x-1 w-16 justify-end">
                      <svg width="17" height="17" viewBox="0 0 24 24" className="text-white">
                        <path
                          fill="currentColor"
                          d="M21 4H16V20H21V4M14 9H9V20H14V9M7 14H2V20H7V14Z"
                        />
                      </svg>
                      <svg width="17" height="17" viewBox="0 0 24 24" className="text-white">
                        <path
                          fill="currentColor"
                          d="M12 20c.55 0 1-.45 1-1s-.45-1-1-1c-2.76 0-5-2.24-5-5c0-.55-.45-1-1-1s-1 .45-1 1c0 3.86 3.14 7 7 7zm0-12C8.14 8 5 11.14 5 15c0 .55-.45 1-1 1s-1-.45-1-1C3 10.03 7.03 6 12 6s9 4.03 9 9c0 .55-.45 1-1 1s-1-.45-1-1c0-3.86-3.14-7-7-7zm0 6c-1.65 0-3 1.35-3 3c0 .55-.45 1-1 1s-1-.45-1-1c0-2.76 2.24-5 5-5s5 2.24 5 5c0 .55-.45 1-1 1s-1-.45-1-1c0-1.65-1.35-3-3-3z"
                        />
                      </svg>
                      <div className="relative w-6 h-3 border border-white/80 rounded-[4px]">
                        <div className="absolute top-0.5 left-0.5 w-[16px] h-[8px] bg-white rounded-sm"></div>
                      </div>
                    </div>
                  </div>

                  {/* Lock Screen Time & Date */}
                  <div className="absolute top-24 left-0 right-0 text-center text-white px-6">
                    <div className="text-7xl font-light tracking-tight">{currentTime}</div>
                    <div className="text-lg font-medium opacity-90 tracking-wide">
                      {currentDayMonth}
                    </div>
                  </div>

                  {/* iOS Notification */}
                  <div className="absolute top-[280px] left-3 right-3">
                    <div className="bg-gray-900/20 backdrop-blur-xl rounded-3xl p-3 shadow-2xl border border-white/20">
                      <div className="flex items-start space-x-3">
                        <div className="w-10 h-10 flex-shrink-0 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center shadow-md">
                          {auth?.user?.profile_image ? (
                            <img
                              src={`${import.meta.env.VITE_BASE_URL}${auth.user.profile_image}`}
                              alt="Profile"
                              className="w-full h-full rounded-lg object-cover"
                            />
                          ) : (
                            <span className="text-white text-xl font-bold">
                              {auth?.user?.business_name?.[0]?.toUpperCase() || 'B'}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-0.5">
                            <span className="text-sm font-semibold text-white/90">
                              {auth?.user?.business_name || 'Your Business'}
                            </span>
                            <span className="text-xs text-white/60">now</span>
                          </div>
                          <p className="text-sm text-white break-words font-medium">
                            {message || 'Your message will appear here...'}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Lock Screen Bottom Elements */}
                  <div className="absolute bottom-8 left-0 right-0 flex justify-between items-center px-12">
                    <div className="w-12 h-12 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-white/80"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M11 3a1 1 0 100 2h6a1 1 0 100-2h-6zM4 7a1 1 0 011-1h10a1 1 0 110 2H5a1 1 0 01-1-1zM4 11a1 1 0 011-1h6a1 1 0 110 2H5a1 1 0 01-1-1zM4 15a1 1 0 011-1h2a1 1 0 110 2H5a1 1 0 01-1-1z" />
                      </svg>
                    </div>
                    <div className="w-12 h-12 bg-black/30 backdrop-blur-md rounded-full flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-6 w-6 text-white/80"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                  </div>

                  {/* Home Indicator Bar */}
                  <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-36 h-1.5 bg-white/90 rounded-full"></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default BusinessCustomerSendMessage
